<template>
  <div class="check-in-display">
    <button class="close-button" v-on:click="$emit('close-check-in-layer')"></button>
    
    <div class="check-in">
      
      <div class="check-in__time">
        <button v-on:click="decrementHours()" class="button check-in__spinner-button" :disabled="checkInDuration.hours === 0">-</button>
        <div>
          <span class="check-in__value">{{ checkInDuration.hours }}</span>
          <span class="check-in__unit">{{ pluralise(checkInDuration.hours, 'hour')}}</span>
        </div>
        <button v-on:click="incrementHours()" class="button check-in__spinner-button" :disabled="checkInDuration.hours === 12">+</button>
        <button v-on:click="decrementMins()" class="button check-in__spinner-button" :disabled="checkInDuration.hours === 0 && checkInDuration.mins === 5">-</button>
        <div>
          <span class="check-in__value">{{ checkInDuration.mins }}</span>
          <span class="check-in__unit">{{ pluralise(checkInDuration.mins, 'minute')}}</span>
        </div>
        <button v-on:click="incrementMins()" class="button check-in__spinner-button" :disabled="checkInDuration.hours === 12">+</button>
      </div> 
      <div class="check-in__options">     
        <button v-on:click="doCheckIn('SPECIFIED_TIME')" class="button check-in__check-in-button">
            Check in until {{ adjustedEndTime }}
            <span class="check-in__end-time-adjusted" v-if="endTimeIsAdjusted">Shortened because next meeting begins at {{ timeFromDateTime(nextEventStart, true) }}</span>
        </button>
        
        <span v-if="nextEvent && !endTimeIsAdjusted">
          or
          <a href v-on:click.prevent="doCheckIn('NEXT_EVENT')" class="check-in__until-next-booking-link">until the next meeting ({{ timeUntilNextEvent }})</a>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import TimeMixins from './TimeMixins.vue';
import ButtonSoundMixins from './ButtonSoundMixins.vue';

export default {
  name: 'CheckIn',
  mixins: [TimeMixins, ButtonSoundMixins],
  props: {
    gCalApi: Object,
    calendarId: String,
    now: Date,
    nextEvent: Object
  },
  data: function() {
    return {
      checkInDuration: { hours: 0, mins: 15 }
    }
  },
  computed: {
    nextEventStart: function() {
      if(this.nextEvent) {
        return new Date((this.nextEvent.start.hasOwnProperty('dateTime')) ? this.nextEvent.start.dateTime : this.nextEvent.start.date);
      } else {
        return null;
      }
    },
    endTime: function() {
      return new Date(this.now.getTime() + (this.checkInDuration.hours*60+this.checkInDuration.mins)*60000);
    },
    adjustedEndTime: function() {
      if(this.endTimeIsAdjusted) {
        return `${this.timeFromDateTime(this.nextEventStart, true)}`;
      } else {
        return this.timeFromDateTime(this.endTime, true);
      }
    },
    endTimeIsAdjusted: function() {
      if(this.nextEvent) {
        return this.endTime > this.nextEventStart;
      } else {
        return false;
      }
    },
    timeUntilNextEvent: function() {
      const diff = this.nextEventStart - this.now;
      return this.timestampDiffToPreciseString(diff);
    }  
  },
  methods: {
    incrementHours: function() {
      if(this.checkInDuration.hours < 12) {
        this.checkInDuration.hours++;
        this.playButtonSound();
      }
    },
    decrementHours: function() {
      if(this.checkInDuration.hours > 0) {
        this.checkInDuration.hours--;
        this.playButtonSound();
      }

      if(this.checkInDuration.hours === 0 && this.checkInDuration.mins === 0) {
        this.checkInDuration.mins = 5;
      }
    },   
    incrementMins: function() {
      if(this.checkInDuration.mins < 55) {
        this.checkInDuration.mins+=5;
      } else if(this.checkInDuration.hours < 12) {
        this.checkInDuration.hours++;
        this.checkInDuration.mins=0;
      }
      this.playButtonSound();
    },
    decrementMins: function() {
      //Minimum booking is 5 mins, so only allow if there are some hours or it won't take us below 5 mins
      if((this.checkInDuration.hours > 0 && this.checkInDuration.mins > 0) || this.checkInDuration.mins > 5) {
        this.checkInDuration.mins-=5;
        this.playButtonSound();
      } else if(this.checkInDuration.hours > 0 && this.checkInDuration.mins == 0) {
        this.checkInDuration.mins = 55;
        this.checkInDuration.hours--;
      }
    },  
    pluralise: function(count, noun) {
      return (count===1) ? noun : noun+'s';
    },
    doCheckIn: function(endTimeType) {
      this.playButtonSound();

      let endTimeToUse;

      if(endTimeType === 'NEXT_EVENT' || this.endTimeIsAdjusted) {
        endTimeToUse = new Date((this.nextEvent.start.hasOwnProperty('dateTime')) ? this.nextEvent.start.dateTime : this.nextEvent.start.date);
      } else {
        endTimeToUse = this.endTime;
      }

      const event = {
        'summary': 'Ad hoc meeting',
        'start': {
          'dateTime': this.now.toISOString()
        },
        'end': {
          'dateTime': endTimeToUse.toISOString()
        },
        'attendees': [
          {'email': this.calendarId, 'responseStatus': 'accepted'}
        ],
        'reminders': {
          'useDefault': false,
          'overrides': [
          ]
        },
        'creator': {
          'displayName': 'You'
        }
      };

      const request = this.gCalApi.client.calendar.events.insert({
        'calendarId': 'primary',
        'resource': event
      });

      this.$emit('check-in-event-added', event); //We emit this first so the parent stops refreshing events before we add ours

      request.execute();
    }     
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/components/colours';
@import '../styles/components/mixins';

.check-in-display {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: $green;
}

.check-in {
  @include vertically-centred;

  &__heading {
    font-size: 10em;
    font-weight: bold;
    text-align: center;
    margin-bottom: 0.5em;
  }

  &__spinner-button {
    font-size: 15em;
    font-weight: 100;
    width: 1.5em;
    height: 1.5em;
    padding: 0;
    text-align: center;
    line-height: 1.6em;
    margin: 0 auto;
  }

  &__time {
    display: grid;
    grid-template-rows: auto auto;
    grid-template-columns: auto 30em auto;
    grid-auto-flow: row;
    column-gap: 5em;
    row-gap: 5em;
    align-items: center;
  }

  &__value {
    font-size: 20em;
    text-align: center;
    text-align: center;
    display: block;
    margin-bottom: -0.1em
  }

  &__unit {
    font-size: 5em;
    text-align: center;
    display: block;
    opacity: 0.8;
  }

  &__options {
    font-size: 7em;
    margin: 1em auto 0 auto; 
    white-space: nowrap;   
    text-align: center;
  }

  &__check-in-button {
    display: block;
    margin: 0 auto 0.5em auto;
  }

  &__end-time-adjusted {
    font-size: 0.6em;
    display: block;
    margin-top: 0.5em;
  }

  &__until-next-booking-link {
    color: $white;
  }
}
</style>
